import { useState } from 'react';
import { useRef } from 'react';
import { DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, Select, Space, Spin } from 'antd';
import CourseService from '@services/courseService';
import { DualSortableLists } from '@components/dualSortableLists/DualSortableLists';

const { Option } = Select;

type DegreeCourseKind = 'required' | 'elective';

type DegreeCourse = Course & { kind: DegreeCourseKind };

interface DegreeCoursesFormProps {
  courses: DegreeCourse[];
  setCourses: (courses: DegreeCourse[]) => void;
}

const LIST_CLASS =
  'max-h-[260px] min-h-[260px] overflow-y-auto rounded border border-dashed border-gray-300 p-2';

export const DegreeCoursesForm = ({ courses, setCourses }: DegreeCoursesFormProps) => {
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState<Course[]>([]);
  const latestRequestId = useRef(0);

  const required = courses.filter((c) => c.kind === 'required');
  const elective = courses.filter((c) => c.kind === 'elective');

  const handleSearch = (searchText: string) => {
    const requestId = ++latestRequestId.current;

    if (!searchText || searchText.length < 2) {
      setLoading(false);
      setSearchData([]);
      return;
    }

    const excludeIds = courses.map((c) => c.id);
    setLoading(true);
    CourseService.search(searchText, 1, excludeIds)
      .then((data) => {
        if (requestId !== latestRequestId.current) {
          return;
        }
        setSearchData(data as Course[]);
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== latestRequestId.current) {
          return;
        }
        setLoading(false);
      });
  };

  const addCourse = (value: number, kind: DegreeCourseKind) => {
    const course = searchData.find((c) => c.id === value);
    if (course && !courses.some((c) => c.id === course.id)) {
      setCourses([...courses, { ...course, kind }]);
    }
  };

  const removeCourse = (id: number) => {
    setCourses(courses.filter((c) => c.id !== id));
  };

  const handleChange = (next: Record<string, DegreeCourse[]>) => {
    const nextRequired = (next.required ?? []).map((c) => ({ ...c, kind: 'required' as const }));
    const nextElective = (next.elective ?? []).map((c) => ({ ...c, kind: 'elective' as const }));
    setCourses([...nextRequired, ...nextElective]);
  };

  const options = searchData.map((course) => (
    <Option value={course.id} key={course.id}>
      {course.name} ({course.id})
    </Option>
  ));

  const renderPicker = (kind: DegreeCourseKind, label: string) => (
    <>
      <h4>{label}</h4>
      <Select
        className="mb-2 w-full"
        placeholder={`Add ${kind === 'required' ? 'a required' : 'an elective'} course`}
        showSearch
        value={null}
        onSearch={handleSearch}
        onChange={(value) => addCourse(Number(value), kind)}
        loading={loading}
        filterOption={false}
        notFoundContent={loading ? <Spin size="small" /> : null}
      >
        {options}
      </Select>
    </>
  );

  const renderItem = (course: DegreeCourse) => (
    <div className="mt-1 cursor-grab border border-gray-400 bg-gray-200 px-2 py-1">
      <Space>
        {course.name} (ID: {course.id})
        <Button
          danger
          size="small"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => removeCourse(course.id)}
        >
          <DeleteOutlined />
        </Button>
        <Button size="small" onMouseDown={(e) => e.stopPropagation()}>
          <a href={`/courses/${course.id}/edit`} target="_blank" rel="noreferrer">
            <LinkOutlined />
          </a>
        </Button>
      </Space>
    </div>
  );

  return (
    <div>
      <label className="ant-form-item-required mb-1 block">Courses</label>
      <p className="mb-2 text-gray-400">
        Drag a course between the lists to change whether it is required or elective.
      </p>
      <DualSortableLists<DegreeCourse>
        lists={[
          { id: 'required', header: renderPicker('required', 'Required courses'), items: required },
          { id: 'elective', header: renderPicker('elective', 'Elective courses'), items: elective },
        ]}
        getItemId={(course) => course.id}
        renderItem={renderItem}
        onChange={handleChange}
        emptyText={(listId) => <p className="m-0 text-gray-400">No {listId} courses yet.</p>}
        listClassName={LIST_CLASS}
      />
    </div>
  );
};
